import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useQuery } from '../lib/useQuery.js';
import { useAuth } from '../lib/auth.js';
import { Page } from '../app/AppShell.js';
import { useToast } from '../components/Toast.js';
import { ShareWithDemoAdmins } from '../components/ShareWithDemoAdmins.js';
import { LayoutStatusBadge, Loading, ErrorNote, EmptyState } from '../components/ui.js';
import { LAYOUT_TEMPLATES } from '../lib/layoutTemplates.js';
import type { FolderNode } from '../lib/types.js';

export function LayoutsScreen(): ReactNode {
  const { can } = useAuth();
  const [folderId, setFolderId] = useState<string | null>(null);
  const layouts = useQuery(() => api.layouts(folderId), [folderId]);
  const folders = useQuery(() => (can('folders.manage') ? api.folders() : Promise.resolve<FolderNode[]>([])), []);
  const navigate = useNavigate();
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);
  // Admins share items with demo administrators, who otherwise only see what they made.
  const canShare = can('users.manage');
  const [sharing, setSharing] = useState<{ id: string; name: string } | null>(null);

  async function move(id: string, target: string | null): Promise<void> {
    setBusyId(id);
    try {
      await api.moveLayout(id, target);
      layouts.refetch();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusyId(null);
    }
  }

  async function publish(id: string): Promise<void> {
    setBusyId(id);
    try {
      await api.publishLayout(id);
      toast.push('Layout published', 'success');
      layouts.refetch();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusyId(null);
    }
  }

  async function remove(id: string, name: string): Promise<void> {
    if (!confirm(`Delete layout “${name}”?`)) return;
    setBusyId(id);
    try {
      await api.deleteLayout(id);
      layouts.refetch();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Page
      title="Layouts"
      actions={
        <div className="row">
          {folders.data && folders.data.length > 0 ? (
            <select value={folderId ?? ''} onChange={(e) => setFolderId(e.target.value || null)}>
              <option value="">All folders</option>
              {folders.data.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          ) : null}
          <button className="btn primary" onClick={() => navigate('/layouts/new')}>
            New layout
          </button>
        </div>
      }
    >
      {can('layouts.edit') ? (
        <>
          <h3 className="small muted">Start from a template</h3>
          <div className="template-strip">
            {LAYOUT_TEMPLATES.map((t) => (
              <button
                key={t.key}
                type="button"
                className="template-tile"
                title={t.description}
                onClick={() => navigate(`/layouts/new?template=${t.key}`)}
              >
                <div className="template-thumb" aria-hidden="true">
                  {t.slots.map((s) => (
                    <span
                      key={s.name}
                      style={{ left: `${s.x * 100}%`, top: `${s.y * 100}%`, width: `${s.w * 100}%`, height: `${s.h * 100}%` }}
                    />
                  ))}
                </div>
                <strong className="small">{t.label}</strong>
              </button>
            ))}
          </div>
        </>
      ) : null}
      {layouts.loading && !layouts.data ? <Loading what="layouts" /> : null}
      {layouts.error ? <ErrorNote error={layouts.error} /> : null}

      {layouts.data ? (
        layouts.data.length === 0 ? (
          <EmptyState>No layouts yet.</EmptyState>
        ) : (
          <div className="card">
            <table className="grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Size</th>
                  <th>Published</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {layouts.data.map((l) => (
                  <tr key={l.id} className="clickable" onClick={() => navigate(`/layouts/${l.id}`)}>
                    <td>{l.name}</td>
                    <td><LayoutStatusBadge status={l.status} /></td>
                    <td className="muted small">
                      {l.width}×{l.height}
                    </td>
                    <td className="muted small">{l.publishedVersion ? `v${l.publishedVersion}` : '—'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row">
                        {folders.data && folders.data.length > 0 ? (
                          <select
                            value={l.folderId ?? ''}
                            disabled={busyId === l.id}
                            onChange={(e) => void move(l.id, e.target.value || null)}
                          >
                            <option value="">Unfiled</option>
                            {folders.data.map((f) => (
                              <option key={f.id} value={f.id}>
                                {f.name}
                              </option>
                            ))}
                          </select>
                        ) : null}
                        {canShare ? (
                          <button className="btn small ghost" onClick={() => setSharing({ id: l.id, name: l.name })}>
                            Share
                          </button>
                        ) : null}
                        <button
                          className="btn small"
                          disabled={busyId === l.id}
                          onClick={() => void publish(l.id)}
                        >
                          Publish
                        </button>
                        <button
                          className="btn small danger"
                          disabled={busyId === l.id}
                          onClick={() => void remove(l.id, l.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
      {sharing !== null ? (
        <ShareWithDemoAdmins type="layout" id={sharing.id} name={sharing.name} onClose={() => setSharing(null)} />
      ) : null}
    </Page>
  );
}
